import React, { useState, useEffect, useMemo } from "react";
import PrizeNavigation from "./PrizeNavigation";
import styles from "./CommissionerDashboard.module.css";
import RuleInput from "./RuleInput.js";
import MembersTable from "./MembersTable.js";
import RuleDisplay from "./RuleDisplay.js";
import Modal from "./Modal";
import AddMembers from "./AddMembers";
import Button from "./Button";
import { useUser } from "../UserContext"; // Import the useUser hook
import { useNavigate, useLocation } from "react-router-dom";
import { fetchUserLeagues } from '../utils/fetchUserLeagues'; // Import fetchUserLeagues
import { isTokenExpired, clearCookie } from "../utils/tokenManagement";  // Import token management utilities
import { useLeague } from '../utils/LeagueContext';
import { mergeMembersWithFees } from '../utils/mergeMembersWithFees';
import AcceptDeclineInvite from "./AcceptDeclineInvite";
import AcceptDeclineInviteEmpty from "./AcceptDeclineInviteEmpty";
import { selectLeagueById } from '../utils/selectLeagueById';
import { sendInviteEmails } from "../utils/inviteService";
import useLeagueAccessGuard from '../hooks/useLeagueAccessGuard';
import API_BASE_URL from '../utils/apiConfig';


const CommissionerDashboard = () => {
  const FRONTEND_BASE_URL = process.env.REACT_APP_FRONTEND_BASE_URL;
  const { user } = useUser();  // Use user data from context
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Use the LeagueContext values instead of local state for the selected league.
  const { leagueId, setLeagueId, selectedLeague, setSelectedLeagueAndId, setSelectedLeague, selectedSeason, setSelectedSeason } = useLeague();
  const handleCreateLeagueClick = () => {
    navigate('/leaguenamesetup');
  };
  // Track the user's role in the selected league (commissioner or member)
  const [userRoleInSelectedLeague, setUserRoleInSelectedLeague] = useState(null);
  const [membersData, setMembersData] = useState([]);
  const [isRuleInputVisible, setRuleInputVisible] = useState(false);
  const [rules, setRules] = useState([]);
  const [isAddMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [leagues, setLeagues] = useState([]); // Track leagues data
  const [message, setMessage] = useState("");  // Initialize message as empty
  const [ruleBeingEdited, setRuleBeingEdited] = useState(null); // Track the rule being edited
  const [members, setMembers] = useState([]); // Needed to add members to the league
  const [inviteStatus, setInviteStatus] = useState(null);
  const [leaguesLoaded, setLeaguesLoaded] = useState(false); // Track if leagues have been loaded
  const [hasSetDefaultLeague, setHasSetDefaultLeague] = useState(false);
  const [isStartSeasonModalOpen, setStartSeasonModalOpen] = useState(false);
  const [startSeasonForm, setStartSeasonForm] = useState({
    season: new Date().getFullYear(),
    hasDues: false,
    duesAmount: '',
    dueDate: '',
  });
  const [startSeasonResult, setStartSeasonResult] = useState(null);
  const [platformSaving, setPlatformSaving] = useState(false);
  const [platformLocal, setPlatformLocal] = useState('other');
  useLeagueAccessGuard(); // Automatically handles auth, sets league, validates membership

  // Handle the message (success or error)
  useEffect(() => {
    if (location?.state?.message) {
      setMessage(location.state.message);  // Set the message if available
    }
  }, [location?.state]);  // Only re-run this when location.state changes

  // Compute currentUserMember so it's available everywhere in the component
  const currentUserMember = useMemo(() => {
    return selectedLeague?.members?.find(
      member => member.userId === user._id
    );
  }, [selectedLeague, user._id]);

  // If the URL does not include the leagueId, redirect to it
  useEffect(() => {
    if (selectedLeague && selectedLeague._id && leagueId !== selectedLeague._id) {
      navigate(`/commissionerdash/${selectedLeague._id}`, { replace: true });
    }
  }, [selectedLeague, leagueId, navigate]);

  // useEffect to log selectedLeague.inviteStatus when it changes
  useEffect(() => {
    if (selectedLeague) {
      console.log("selectedLeague.inviteStatus (from useEffect):", selectedLeague.inviteStatus);
      console.log("Condition result:", selectedLeague.inviteStatus === 'invited');
    }
  }, [selectedLeague]);

  // When selectedLeague updates, update user role, members, and rules.
  useEffect(() => {
    if (selectedLeague) {
      // Get the user's role in the selected league from members array
      const currentUserMember = selectedLeague.members?.find(
        member => member.userId === user._id
      );

    const leagueRole = currentUserMember?.role;
    setUserRoleInSelectedLeague(leagueRole);  // Set the role (commissioner/member)

    // Extract members from the selected league and merge members with fees
    if (selectedLeague.members) {
      const mergedMembers = mergeMembersWithFees(
        selectedLeague.members,
        selectedLeague.fees || [],
        selectedSeason
      );
      setMembersData(mergedMembers);
    } else {
      setMembersData([]);
    }

    // Add rules handling
    if (selectedLeague.rules) {
      setRules(selectedLeague.rules);
    } else {
      setRules([]);
    }
  }
}, [selectedLeague, user._id, selectedSeason]);

  useEffect(() => {
  const getLeagues = async () => {
    if (!user || !user._id) return;

    // Avoid resetting selectedLeague if already enriched
    if (selectedLeague?.inviteStatus) {
      return;
    }

    try {
      const userLeagues = await fetchUserLeagues(user._id);

      const filteredLeagues = userLeagues.filter(
        league =>
          league.inviteStatus === 'active' ||
          league.inviteStatus === 'invited'
      );

      setLeagues(filteredLeagues);

      const storedLeagueId = localStorage.getItem("leagueId");
      setLeaguesLoaded(true); // Mark fetch complete

      const matchingLeague = filteredLeagues.find(
        (l) => String(l._id) === storedLeagueId || String(l.leagueId) === storedLeagueId
      );

      if (matchingLeague) {
        await setSelectedLeagueAndId(
          matchingLeague.leagueId,
          false,
          matchingLeague
        );
      } else if (filteredLeagues.length > 0) {
        const fallback = filteredLeagues[0];
        await setSelectedLeagueAndId(
          fallback.leagueId,
          true,
          fallback
        );
      } else {
        setSelectedLeague(null);
        localStorage.removeItem("leagueId");
      }
    } catch (error) {
      setError("Failed to fetch leagues");
      setLeaguesLoaded(true); // Still mark as complete on error
    }
  };

  getLeagues();
}, [user._id, selectedLeague]);

  const handleSelectLeague = async (selectedLeagueId) => {
  if (!selectedLeagueId) {
    return;
  }

  try {
    setLoading(true);
    setError(null);

    await selectLeagueById({
      leagueId: selectedLeagueId,
      leagues,
      setSelectedLeague,
      setInviteStatus,
      setError
    });
    
  } catch (error) {
    setError(error.message);
  } finally {
    setLoading(false);
  }
};

  const handleAddRuleClick = (e) => {
    e.preventDefault();
    setRuleBeingEdited(null); // Clear any rule being edited
    setRuleInputVisible(true); // Show the RuleInput component
  };

  const handleEditRuleClick = (rule) => {
    setRuleBeingEdited(rule); // Set the rule being edited
    setRuleInputVisible(true); // Show the RuleInput component
  };

  const handleSaveRule = async (ruleData) => {
    try {
      if (!selectedLeague) {
        throw new Error("No league selected");
      }

    const formattedRule = {
      type: ruleData.type,
      details: ruleData.details,
    };

    if (!formattedRule.type || !formattedRule.details) {
      throw new Error("Rule type and details are required");
    }

    let updatedRules;
    if (ruleBeingEdited) {
      // Editing an existing rule
      const response = await fetch(
        `${API_BASE_URL}/api/leagues/${selectedLeague._id}/rules/${ruleBeingEdited._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(formattedRule),
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || "Failed to update rule");
      }

      // Update the rule in the local state
      updatedRules = rules.map((rule) =>
        rule._id === ruleBeingEdited._id ? responseData : rule
      );
    } else {
      // Adding a new rule
      const response = await fetch(
        `${API_BASE_URL}/api/leagues/${selectedLeague._id}/rules`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(formattedRule),
        }
      );

      let responseData = await response.json();

      if (Array.isArray(responseData)) {
        if (responseData.length === 0) {
          throw new Error("API returned an empty array for the new rule.");
        }
        // Extract the last rule object from the array
        responseData = responseData[responseData.length - 1];
      }

      if (!response.ok) {
        throw new Error(responseData.message || "Failed to save rule");
      }

      if (!responseData.type || !responseData.details) {
        throw new Error("New rule is invalid");
      }

      // Append the new rule to the local state
      updatedRules = [...rules, responseData];
    }

      // Update rules in both local state and context
      setRules(updatedRules); // Update the local rules state
      setSelectedLeague((prevLeague) => ({
        ...prevLeague,
        rules: updatedRules, // Update the rules in the selectedLeague context
    }));

      setRuleInputVisible(false); // Hide the RuleInput component
      setRuleBeingEdited(null); // Clear the rule being edited
    } catch (error) {
      console.error("Error details:", {
        message: error.message,
        selectedLeague: selectedLeague,
        ruleData: ruleData,
      });
      alert("Oops. There was a problem saving the rule. Please try again.");
      throw error;
    }
  };

  const handleCancelRule = () => {
    setRuleInputVisible(false); // Hide the RuleInput component
    setRuleBeingEdited(null); // Clear the rule being edited
  };

  const handleDeleteRule = async () => {
    if (!ruleBeingEdited || !ruleBeingEdited._id) {
      console.error("Cannot delete a new rule that hasn't been saved.");
      return;
    }

    // Show a confirmation dialog
    const confirmDelete = window.confirm("Are you sure you want to delete this rule?");
    if (!confirmDelete) {
      return; // Exit if the user cancels
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/leagues/${selectedLeague._id}/rules/${ruleBeingEdited._id}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete rule");
     }

      // Remove the rule from the local state
      const updatedRules = rules.filter((rule) => rule._id !== ruleBeingEdited._id);
        setRules(updatedRules);
        setSelectedLeague({
        ...selectedLeague,
        rules: updatedRules,
     });

      setRuleInputVisible(false); // Hide the RuleInput component
      setRuleBeingEdited(null); // Clear the rule being edited
    } catch (error) {
      console.error("Error deleting rule:", error);
      alert("Oops. There was a problem deleting the rule. Please try again.");
    }
  };

  const handleAddMemberClick = () => {
    setAddMemberModalOpen(true); // Open modal when "Add member" is clicked
  };

  const handleCloseModal = () => {
    setAddMemberModalOpen(false); // Close modal
  };

  const handleDeleteMember = async (memberId) => {
    if (!selectedLeague || !memberId) return;
  
    const memberToDelete = membersData.find((m) => m._id === memberId);
    if (!memberToDelete) return;
  
    if (memberToDelete.role === "commissioner") {
      alert("You cannot delete the commissioner of the league.");
      return;
    }
  
    const confirmDelete = window.confirm("Are you sure you want to remove this member from the league?");
    if (!confirmDelete) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/leagues/remove-user`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify({
          userId: memberToDelete.userId,
          leagueId: selectedLeague._id,
        }),
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to remove user from league');
      }
  
      // 1. Update local member list
      setMembersData((prev) => prev.filter((m) => m._id !== memberId));
  
      // 2. Update league's member list
      setSelectedLeague((prev) => ({
        ...prev,
        members: prev.members.filter((m) => m.userId !== memberToDelete.userId),
      }));
  
    } catch (err) {
      console.error("Error removing member:", err.message);
      alert("Failed to remove member: " + err.message);
    }
  };   

  const handleFeeChange = async (memberId, currentPaidStatus) => {
    const updatedPaidStatus = !currentPaidStatus;
    const currentYear = new Date().getFullYear();
    if (!selectedLeague || !memberId) return;

    const member = membersData.find((m) => m._id === memberId);
    if (!member) {
      console.error("Member not found for ID:", memberId);
      return;
    }
  
    try {
      const response = await fetch(`${API_BASE_URL}/api/leagues/${selectedLeague._id}/fees`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify({
          userId: member.userId,
          year: currentYear,
          paid: updatedPaidStatus,
        }),
      });
  
      if (response.ok) {
        const data = await response.json();
        // Update the local state to reflect the new fee status
        setMembersData((prevMembers) =>
          prevMembers.map((m) =>
            m._id === memberId ? { ...m, feesPaid: updatedPaidStatus } : m
          )
        );
      } else {
        const errorData = await response.json();
        console.error('Failed to update fee status:', errorData);
      }
    } catch (err) {
      console.error('Error sending update fee request:', err);
    }
  };
  
  const handleStartSeasonClick = () => {
    setStartSeasonForm({
      season: new Date().getFullYear(),
      hasDues: selectedLeague?.defaultDuesAmount ? true : false,
      duesAmount: selectedLeague?.defaultDuesAmount || '',
      dueDate: '',
    });
    setStartSeasonResult(null);
    setStartSeasonModalOpen(true);
  };

  const handleStartSeasonConfirm = async () => {
    try {
      if (startSeasonForm.hasDues && !startSeasonForm.dueDate) {
        setMessage('Please enter a dues deadline before starting the season.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/leagues/${selectedLeague._id}/start-season`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify({
          season: startSeasonForm.season,
          hasDues: startSeasonForm.hasDues,
          duesAmount: startSeasonForm.hasDues ? Number(startSeasonForm.duesAmount) : null,
          dueDate: startSeasonForm.hasDues ? startSeasonForm.dueDate : null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setMessage(`Error: ${data.message}`);
        setStartSeasonModalOpen(false);
        return;
      }

      setStartSeasonResult(data);

      // Re-fetch the updated league to refresh fee status in the UI
      const leagueResponse = await fetch(`${API_BASE_URL}/api/leagues/${selectedLeague._id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
      });

      if (leagueResponse.ok) {
        const updatedLeague = await leagueResponse.json();
        setSelectedLeague(updatedLeague);
      }

      setStartSeasonModalOpen(false);

      // Build success message
      let successMsg = `✅ ${startSeasonForm.season} season started! `;
      if (data.pollsArchived > 0) successMsg += `${data.pollsArchived} poll(s) archived. `;
      if (data.feesReset > 0) successMsg += `${data.feesReset} member dues reset. `;
      if (data.prizesCreated > 0) successMsg += `${data.prizesCreated} prize(s) copied from last season. `;
      if (data.activePollWarning) successMsg += `⚠️ Note: you have an active poll that hasn't been closed yet.`;

      setMessage(successMsg);
      setTimeout(() => setMessage(''), 8000);

    } catch (err) {
      console.error('Error starting new season:', err);
      setMessage('Error starting new season. Please try again.');
      setStartSeasonModalOpen(false);
    }
  };

  // Sync platformLocal when selectedLeague changes
  useEffect(() => {
    if (selectedLeague?.platform) {
      setPlatformLocal(selectedLeague.platform);
    }
  }, [selectedLeague?.platform]);

  const handleSavePlatform = async () => {
    if (!selectedLeague) return;
    setPlatformSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/leagues/${selectedLeague._id}/platform`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ platform: platformLocal }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Failed to save platform');
      }
      setSelectedLeague(prev => ({ ...prev, platform: platformLocal }));
      setMessage('Platform saved.');
      setTimeout(() => setMessage(''), 4000);
    } catch (err) {
      setMessage('Error saving platform: ' + err.message);
    } finally {
      setPlatformSaving(false);
    }
  };

  const handleAddMember = (newMembers) => {
    setMembers((prevMembers) => [...prevMembers, ...newMembers]);
  };

  const handleSaveMembers = async (newMembers) => {

    if (!selectedLeague || !selectedLeague._id) {
      console.error("No league selected. Cannot add members.");
      alert("Please select a league before adding members.");
      return;
    }

    try {
      // Step 1: Update the league's members array in the database
      const response = await fetch(`${API_BASE_URL}/api/leagues/${selectedLeague._id}/members`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify({
          members: newMembers.map((member) => ({
            ...member,
            role: member.role || "member", // Ensure the role is included, default to "member"
          })),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update league members.");
      }

      const updatedLeague = await response.json();

      // Step 2: Send invite emails
      const invitees = newMembers.map(member => ({
        to: member.email,
        name: member.name,
        league_name: selectedLeague.leagueName,
        action_url: `${FRONTEND_BASE_URL}/createaccount`, // Customize later when adding redirect logic to something like this: http://localhost:3000/login?redirect=/commissionerdash/${selectedLeague._id}
      }));

      await sendInviteEmails(invitees); // dryRun flag controlled by backend env 

      // Step 3:Update the frontend state
      setMembersData((prevMembers) => [...prevMembers, ...newMembers]);
      setSelectedLeague((prevLeague) => ({
        ...prevLeague,
        members: [...prevLeague.members, ...newMembers],
      }));

      alert("Members added successfully!");
    } catch (error) {
      console.error("Error adding members:", error);
      alert("Failed to add members. Please try again.");
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.navContainer}> 
        <PrizeNavigation navigationButtonArea="/2813821-1@2x.png" leagueId={selectedLeague ? selectedLeague._id : ""} />
      </div>
      <div className={styles.dashboardContent}>
        <div className={styles.memberArea}>
          <div className={styles.header}>
            <p className={styles.spicyGridiron}>
              {typeof selectedLeague?.inviteStatus === "string"
                ? selectedLeague.leagueName
                : "Select a league to see league details"}
            </p>
            <p className={styles.commissionerMode}>
            {/* {selectedLeague ? selectedLeague.leagueName : ""} */}
            </p>
          </div>

          {/* Show message (success or error) */}
          {message && (
            <div className={`${styles.message} ${message.includes("success") ? styles.success : styles.error}`}>
              {message}
            </div>
          )}

          {/* Conditionally Render the League Selection or Create League Button */}
          {!leaguesLoaded ? null : leagues.length === 0 ? (
            <div className={styles.createLeague}>
              <p>Create a league or ask your league commissioner invite you to one.</p>
              <Button label={"Create league"} onClick={handleCreateLeagueClick} />
            </div>
          ) : (
            <div className={styles.selectLeague}>
              <select
                value={selectedLeague ? selectedLeague.leagueId : ""}
                onChange={(e) => handleSelectLeague(e.target.value)}
              >
                <option value="">Select a League</option>
                  {leagues.map((league) => (
                <option key={league.leagueId} value={league.leagueId} style={{ color: '#000', backgroundColor: '#fff' }}>
                  {league.leagueName}
                </option>
              ))}
              </select>

              {selectedLeague && (
                <div className={styles.seasonSelectorContainer}>
                  <select
                    className={styles.seasonSelector}
                    value={selectedSeason}
                    onChange={(e) => setSelectedSeason(Number(e.target.value))}
                  >
                    {Array.from(
                      new Set(
                        (selectedLeague.fees || [])
                          .map(f => f.year)
                          .filter(Boolean)
                          .concat([new Date().getFullYear()])
                      )
                    )
                      .sort((a, b) => b - a)
                      .map(year => (
                        <option key={year} value={year}>{year} Season</option>
                      ))}
                  </select>
                </div>
              )}

              {selectedLeague && userRoleInSelectedLeague === 'commissioner' && (
                <div className={styles.startSeason}>
                  <button
                    className={styles.startSeasonButton}
                    onClick={handleStartSeasonClick}
                  >
                    Start New Season
                  </button>
                </div>
              )}

              {selectedLeague && userRoleInSelectedLeague === 'commissioner' && (
                <div className={styles.platformSetting}>
                  <label className={styles.platformLabel}>Fantasy platform</label>
                  <div className={styles.platformRow}>
                    <select
                      className={styles.platformSelect}
                      value={platformLocal}
                      onChange={e => setPlatformLocal(e.target.value)}
                    >
                      <option value="espn">ESPN</option>
                      <option value="sleeper">Sleeper</option>
                      <option value="other">Other</option>
                    </select>
                    <button
                      className={styles.platformSaveButton}
                      onClick={handleSavePlatform}
                      disabled={platformSaving || platformLocal === selectedLeague?.platform}
                    >
                      {platformSaving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {selectedLeague?.inviteStatus === 'invited' ? (
            <AcceptDeclineInvite
              leagueId={selectedLeague._id}
              userId={user._id}
            />
          ) : (
        <>
          <img
            className={styles.memberButtonArea}
            loading="lazy"
            alt="Image of an athlete"
            src="/2348758-1@2x.png"
          />
          <h1 className={styles.members}>Members</h1>

          {/* Conditionally Render Members and Rules */}
          {selectedLeague && (
            <>
              {userRoleInSelectedLeague === 'commissioner' && (
                <div className={styles.addMember} onClick={handleAddMemberClick}>
                  + Add member
                </div>
              )}

              {isAddMemberModalOpen && (
                <Modal onClose={handleCloseModal}>
                  <AddMembers
                    isModal={true}
                    onCancel={handleCloseModal}
                    onClose={handleCloseModal}
                    onSave={(newMembers) => {
                      handleAddMember(newMembers); // Update the members state in InviteMembers
                      handleSaveMembers(newMembers); // Pass the combined array directly
                    }}
                  />
                </Modal>
              )}

              {isStartSeasonModalOpen && (
                <Modal onClose={() => setStartSeasonModalOpen(false)}>
                  <div className={styles.startSeasonModal}>
                    <h2>Start {startSeasonForm.season} Season</h2>
                    <p className={styles.startSeasonLeagueName}>{selectedLeague?.leagueName}</p>

                    <div className={styles.startSeasonField}>
                      <label className={styles.startSeasonLabel}>Does your league have dues?</label>
                      <div className={styles.startSeasonToggle}>
                        <label>
                          <input
                            type="radio"
                            name="hasDues"
                            value="yes"
                            checked={startSeasonForm.hasDues === true}
                            onChange={() => setStartSeasonForm({ ...startSeasonForm, hasDues: true })}
                          />
                          {' '}Yes
                        </label>
                        <label style={{ marginLeft: '16px' }}>
                          <input
                            type="radio"
                            name="hasDues"
                            value="no"
                            checked={startSeasonForm.hasDues === false}
                            onChange={() => setStartSeasonForm({ ...startSeasonForm, hasDues: false })}
                          />
                          {' '}No
                        </label>
                      </div>
                    </div>

                    {startSeasonForm.hasDues && (
                      <>
                        <div className={styles.startSeasonField}>
                          <label className={styles.startSeasonLabel}>Dues amount ($)</label>
                          <input
                            type="number"
                            className={styles.startSeasonInput}
                            value={startSeasonForm.duesAmount}
                            onChange={(e) => setStartSeasonForm({ ...startSeasonForm, duesAmount: e.target.value })}
                            placeholder="e.g. 50"
                          />
                        </div>
                        <div className={styles.startSeasonField}>
                          <label className={styles.startSeasonLabel}>Dues deadline</label>
                          <input
                            type="date"
                            className={styles.startSeasonInput}
                            value={startSeasonForm.dueDate}
                            onChange={(e) => setStartSeasonForm({ ...startSeasonForm, dueDate: e.target.value })}
                          />
                        </div>
                      </>
                    )}

                    <div className={styles.startSeasonInfo}>
                      <p>Starting a new season will:</p>
                      <ul>
                        <li>Archive last season's closed polls</li>
                        {startSeasonForm.hasDues && <li>Reset payment status for all members and send dues reminders</li>}
                        <li>Copy last season's prizes to {startSeasonForm.season} (if you had any)</li>
                      </ul>
                    </div>

                    <div className={styles.startSeasonActions}>
                      <button
                        className={styles.startSeasonCancelButton}
                        onClick={() => setStartSeasonModalOpen(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className={styles.startSeasonConfirmButton}
                        onClick={handleStartSeasonConfirm}
                      >
                        Start {startSeasonForm.season} Season →
                      </button>
                    </div>
                  </div>
                </Modal>
              )}
            </>
          )}

          <div className={styles.membersTableContainer}>
            <MembersTable
              data={membersData}
              userRole={userRoleInSelectedLeague}
              handleDeleteMember={handleDeleteMember}
              handleFeeChange={handleFeeChange}
            />
          </div>
        </>
        )}
        </div>

        {selectedLeague?.inviteStatus === 'invited' ? (
        <AcceptDeclineInviteEmpty
          leagueId={selectedLeague._id}
          userId={user._id}
          // ...any other props you need to pass
        />
        ) : (
        <>
        <div className={styles.ruleArea}>
          <div className={styles.ruleSpace}>
          </div>
          <img
            className={styles.memberButtonArea}
            loading="lazy"
            alt="Image of a whistle"
            src="/1044406-1@2x.png"
          />
          <div className={styles.ruleDisplay}>
            <h1 className={styles.rules1}>Rules</h1>
          </div>
            {userRoleInSelectedLeague === 'commissioner' && (
              <div className={styles.ruleAdderArea}>
                <a className={styles.addRule} href="#" onClick={handleAddRuleClick}>
                  + Add rule
                </a>
              </div>
            )}

              <div className={styles.ruleInputContainer}>
                {isRuleInputVisible && (
                  <RuleInput
                    ruleType={ruleBeingEdited?.type || ''} // Pre-fill the rule type if editing
                    ruleDetails={ruleBeingEdited?.details || ''} // Pre-fill the rule details if editing
                    onSave={handleSaveRule} // Handle saving the rule
                    onCancel={handleCancelRule} // Handle canceling the rule
                    onDelete={handleDeleteRule} // Handle deleting the rule
                    isNewRule={!ruleBeingEdited} // Determine if it's a new rule
                  />
                )}
                {rules.map((rule, index) => {
                  if (Array.isArray(rule)) {
                    console.error("Invalid rule format: rule is an array instead of an object", rule);
                    return null; // Skip rendering invalid rules
                  }

                  return (
                    <RuleDisplay
                      key={index}
                      rule={rule} // Pass the rule object to RuleDisplay
                      ruleType={rule.type || "Unknown Type"} // Provide a fallback for ruleType
                      ruleDetails={rule.details || "No details provided"} // Provide a fallback for ruleDetails
                      handleEditRuleClick={() => handleEditRuleClick(rule)} // Pass the handler as a prop
                      canEdit={userRoleInSelectedLeague === 'commissioner'} // Allow editing only for commissioners
                    />
                  );
                })}
              </div>
          </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CommissionerDashboard;
